/* ==================================================================
   NOSH — Shared horizontal rail controller + scroll progress system
   ------------------------------------------------------------------
   ONE carousel behaviour for the whole Nosh custom layer. There is
   deliberately no second carousel framework in this theme.

   PART 1 — <nosh-rail-scroller> (unchanged contract, Pass 1.1B1)
     [data-rail-track]  the scrolling flex track (also the focusable
                        keyboard target)
     [data-rail-prev]   optional previous button
     [data-rail-next]   optional next button

     - native horizontal scrolling + CSS scroll-snap (no JS transforms,
       so touch scrolling is never trapped or hijacked)
     - prev/next page by one visible track width
     - ArrowLeft / ArrowRight when the track has focus
     - buttons disable at both boundaries
     - NO autoplay, ever. These are informational rails.
     - honours prefers-reduced-motion by falling back to instant jumps

   PART 2 — [Pass F1] shared scroll progress indicator
   ------------------------------------------------------------------
   The founders asked for a clear indication that a belt scrolls, now
   that every mobile belt shows exactly TWO complete cards and no
   longer relies on a half-visible third card as the cue.

   This controller attaches to EVERY `[data-rail-track]` in the
   document, whether or not that track sits inside a
   <nosh-rail-scroller>. sections/nosh-product-rail.liquid ships its own
   <nosh-product-rail> element and is picked up here too, so one
   implementation covers the category shelf, metrics, product rails,
   related-products rail and any future Nosh belt using the same
   contract.

   Design decisions:
     - The indicator DOM is injected by JS rather than added to every
       section file. That keeps section templates untouched and means a
       belt can never ship a stale or duplicated indicator.
     - It is display-only: aria-hidden, not focusable, not a control.
       The track itself remains the keyboard target, so the indicator
       is never the sole affordance for scrolling.
     - It hides whenever the content fits, so a non-overflowing belt
       shows nothing at all.
     - All maths is guarded against divide-by-zero and NaN.
     - One delegated scroll handler per track, rAF-throttled, passive.
     - ResizeObserver + MutationObserver per track so the indicator
       stays correct after a resize, an orientation change, a Shopify
       section reload, a collection facet re-render, or an async
       recommendations fetch.
     - Everything is torn down on shopify:section:unload, so repeated
       Theme Editor reloads cannot leak observers or listeners.
   ================================================================== */

if (!customElements.get('nosh-rail-scroller')) {
  customElements.define(
    'nosh-rail-scroller',
    class extends HTMLElement {
      connectedCallback() {
        if (this._inited) return;
        this.track = this.querySelector('[data-rail-track]');
        this.prev = this.querySelector('[data-rail-prev]');
        this.next = this.querySelector('[data-rail-next]');
        if (!this.track) return;
        this._inited = true;

        this.onPrev = () => this.page(-1);
        this.onNext = () => this.page(1);
        if (this.prev) this.prev.addEventListener('click', this.onPrev);
        if (this.next) this.next.addEventListener('click', this.onNext);

        this.onKey = (e) => {
          if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.page(1);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.page(-1);
          }
        };
        this.track.addEventListener('keydown', this.onKey);

        this.sync = () => this.updateArrows();
        this.track.addEventListener('scroll', this.sync, { passive: true });
        window.addEventListener('resize', this.sync);
        this.updateArrows();
      }

      disconnectedCallback() {
        if (this.prev) this.prev.removeEventListener('click', this.onPrev);
        if (this.next) this.next.removeEventListener('click', this.onNext);
        if (this.track) {
          this.track.removeEventListener('keydown', this.onKey);
          this.track.removeEventListener('scroll', this.sync);
        }
        window.removeEventListener('resize', this.sync);
        this._inited = false;
      }

      page(dir) {
        const reduce =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({
          left: dir * this.track.clientWidth,
          behavior: reduce ? 'auto' : 'smooth',
        });
      }

      updateArrows() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const x = this.track.scrollLeft;
        const atStart = x <= 2;
        const atEnd = x >= max - 2;
        if (this.prev) this.prev.disabled = atStart;
        if (this.next) this.next.disabled = atEnd || max <= 0;
      }
    }
  );
}

/* ------------------------------------------------------------------
   Pass F2 - explicit carousel registry + shared scroll progress
   ------------------------------------------------------------------
   Pass F1 attached only to [data-rail-track]. That contract reached the
   category shelf, the metrics belt, the homepage product rails and the
   PDP related rail, and nothing else. Measured on the live beta, these
   customer-facing belts were silently missed because they never carried
   the attribute:

     .nosh-quotes__track               Notes from our customers
     .nosh-community-reviews__track    From the Nosh community
     .nosh-frail__track                founder collection rails
     .nosh-collection-reel__viewport   collection live product reel
     .nosh-collection-seo-hero__chips  collection discovery chips

   F2 replaces the guess with an explicit, documented registry. Every
   belt that should carry an indicator is named here, and every belt
   that must NOT is named in EXCLUDE. Adding a future belt is a one-line
   change in one file, and nothing is inferred from unrelated classes.

   Anything matched is stamped with the canonical hooks
   data-nosh-carousel (on the section root) and
   data-nosh-carousel-track (on the scroller), so markup, CSS and JS
   all share one vocabulary from here on. Sections may also opt in
   directly by authoring data-nosh-carousel-track in their own markup.
   ------------------------------------------------------------------ */
(function () {
  'use strict';

  var VERSION = 2;

  // A Theme Editor reload can re-execute this file. Re-running must
  // rescan, never double-bind. An older version teardown is honoured so
  // a cached F1 copy cannot leave orphaned listeners behind.
  if (window.NoshRails && window.NoshRails.version === VERSION) {
    window.NoshRails.refresh();
    return;
  }
  if (window.NoshRails && typeof window.NoshRails.teardown === 'function') {
    try {
      window.NoshRails.teardown();
    } catch (e) {
      /* never let a stale controller block the new one */
    }
  }

  /* Belts that GET an indicator. Explicit by design. */
  var TRACK_SELECTORS = [
    '[data-nosh-carousel-track]',
    '[data-rail-track]',
    '.nosh-cats__track',
    '.nosh-metrics__track',
    '.nosh-rail__track',
    '.nosh-rrail__track',
    '.nosh-frail__track',
    '.nosh-quotes__track',
    '.nosh-community-reviews__track',
    '.nosh-ff__rail',
    '.nosh-collection-reel__viewport',
    '.nosh-collection-seo-hero__chips'
  ];

  /* Belts that must NEVER get one. The hero and the ribbon have their
     own controls or their own motion language; the media gallery,
     thumbnails, accordions, facet panels and the product grid are not
     carousels at all, they merely happen to compute overflow-x. */
  var EXCLUDE_SELECTORS = [
    '.nosh-heroc__viewport',
    '[data-heroc-viewport]',
    '.nosh-ribbon__track',
    '.product__media-list',
    '.thumbnail-list',
    '.accordion__content',
    '.facets__display',
    '.mobile-facets__inner',
    '.mobile-facets__main',
    '.facets-layout',
    /* Phase A. The collection product grid is still excluded by default:
       on desktop it is a static grid that merely happens to compute
       overflow-x, exactly as F2 recorded. It becomes a real belt only in
       the mobile two-card deck, and only then does
       sections/main-collection-product-grid.liquid server-render the
       canonical data-nosh-carousel-track opt-in. Lifting the exclusion
       for precisely that attribute keeps the rule explicit: nothing is
       inferred, and a grid without the opt-in is still never touched.
       The attribute is part of the server HTML, so a facet or sort
       re-render brings it back and the indicator is re-attached by the
       document observer below. */
    '.product-grid:not([data-nosh-carousel-track])',
    '.js-filter'
  ];

  var SELECTOR = TRACK_SELECTORS.join(',');
  var EXCLUDE = EXCLUDE_SELECTORS.join(',');
  var MIN_OVERFLOW = 2; // px tolerance for sub-pixel layout rounding
  var entries = [];
  var listening = false;

  function clamp01(n) {
    if (!isFinite(n) || isNaN(n)) return 0;
    return n < 0 ? 0 : n > 1 ? 1 : n;
  }

  function excluded(el) {
    try {
      return el.matches(EXCLUDE) || !!el.closest('.nosh-heroc, .nosh-ribbon');
    } catch (e) {
      return false;
    }
  }

  function buildIndicator() {
    var bar = document.createElement('div');
    bar.className = 'nosh-scrollbar';
    bar.setAttribute('data-nosh-carousel-progress', '');
    // Display-only. Never announced as a control, never focusable: the
    // track itself stays the keyboard target, so the indicator is never
    // the sole affordance for scrolling.
    bar.setAttribute('aria-hidden', 'true');
    bar.hidden = true;
    var thumb = document.createElement('span');
    thumb.className = 'nosh-scrollbar__thumb';
    bar.appendChild(thumb);
    return bar;
  }

  function measure(entry) {
    var track = entry.track;
    var bar = entry.bar;
    if (!track.isConnected || !bar.isConnected) return;

    var client = track.clientWidth;
    var scroll = track.scrollWidth;
    var max = scroll - client;

    // No overflow, or an unmeasurable / zero-width / display:none track:
    // show nothing at all.
    if (!(max > MIN_OVERFLOW) || client <= 0 || !isFinite(scroll)) {
      if (!bar.hidden) bar.hidden = true;
      return;
    }
    if (bar.hidden) bar.hidden = false;

    var ratio = clamp01(client / scroll); // visible proportion
    var progress = clamp01(track.scrollLeft / max);

    // Keep the thumb usable on very long belts.
    var widthPct = Math.max(ratio * 100, 12);
    var offsetPct = progress * (100 - widthPct);

    bar.style.setProperty('--nosh-sb-w', widthPct.toFixed(3));
    bar.style.setProperty('--nosh-sb-x', offsetPct.toFixed(3));
  }

  function attach(track) {
    if (!track || track.nodeType !== 1) return;
    if (track.dataset.noshSbBound === '1') return;
    // Section authors may deliberately keep the swipe rail but hide its
    // display-only progress cue. The native scrollbar remains suppressed by
    // the section CSS, preserving the approved no-black-scrollbar treatment.
    if (track.getAttribute('data-nosh-progress') === 'false') return;
    if (excluded(track)) return;

    var parent = track.parentNode;
    if (!parent || parent.nodeType !== 1) return;

    // Canonical hooks, stamped once, so markup / CSS / JS share one
    // vocabulary even where the section file predates it.
    track.setAttribute('data-nosh-carousel-track', '');
    var root = track.closest('.shopify-section') || parent;
    if (root && root.setAttribute) root.setAttribute('data-nosh-carousel', '');

    track.dataset.noshSbBound = '1';

    var bar = buildIndicator();
    // Directly after the track, inside the same wrapper, so it inherits
    // the section's own horizontal gutter rather than needing its own.
    if (track.nextSibling) {
      parent.insertBefore(bar, track.nextSibling);
    } else {
      parent.appendChild(bar);
    }

    var entry = { track: track, bar: bar, ticking: false, ro: null, mo: null };

    entry.request = function () {
      if (entry.ticking) return;
      entry.ticking = true;
      var run = function () {
        entry.ticking = false;
        measure(entry);
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(run);
      else window.setTimeout(run, 66);
    };

    track.addEventListener('scroll', entry.request, { passive: true });

    // Width changes: viewport resize, orientation change, font loading,
    // a container that only settles after images decode.
    if ('ResizeObserver' in window) {
      entry.ro = new ResizeObserver(entry.request);
      entry.ro.observe(track);
    }

    // Content changes: collection facet re-render, async product
    // recommendations, Theme Editor block add or remove.
    if ('MutationObserver' in window) {
      entry.mo = new MutationObserver(entry.request);
      entry.mo.observe(track, { childList: true, subtree: false });
    }

    entries.push(entry);
    measure(entry);
  }

  function detach(entry) {
    entry.track.removeEventListener('scroll', entry.request);
    if (entry.ro) entry.ro.disconnect();
    if (entry.mo) entry.mo.disconnect();
    delete entry.track.dataset.noshSbBound;
    if (entry.bar && entry.bar.parentNode) entry.bar.parentNode.removeChild(entry.bar);
  }

  function prune() {
    for (var i = entries.length - 1; i >= 0; i--) {
      if (!entries[i].track.isConnected) {
        detach(entries[i]);
        entries.splice(i, 1);
      }
    }
  }

  function refresh() {
    prune();
    var tracks;
    try {
      tracks = document.querySelectorAll(SELECTOR);
    } catch (e) {
      return;
    }
    for (var i = 0; i < tracks.length; i++) attach(tracks[i]);
    for (var j = 0; j < entries.length; j++) entries[j].request();
  }

  function teardown() {
    for (var i = entries.length - 1; i >= 0; i--) detach(entries[i]);
    entries.length = 0;
    window.removeEventListener('resize', onWindowResize);
    window.removeEventListener('orientationchange', onWindowResize);
    document.removeEventListener('shopify:section:load', refresh);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    if (bodyObserver) bodyObserver.disconnect();
    listening = false;
  }

  var resizeTicking = false;
  function onWindowResize() {
    if (resizeTicking) return;
    resizeTicking = true;
    var run = function () {
      resizeTicking = false;
      for (var i = 0; i < entries.length; i++) measure(entries[i]);
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else window.setTimeout(run, 66);
  }

  function onSectionUnload() {
    // Give Shopify a tick to remove the old DOM before pruning.
    window.setTimeout(prune, 0);
  }

  window.addEventListener('resize', onWindowResize);
  window.addEventListener('orientationchange', onWindowResize);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  listening = true;

  // Late-arriving belts that fire no Shopify event: async product
  // recommendations, facet replacement of the product grid, a
  // third-party app injecting content. One coalesced observer for the
  // whole document is cheaper than one per candidate.
  var bodyObserver = null;
  var bodyTick = null;
  if ('MutationObserver' in window) {
    bodyObserver = new MutationObserver(function () {
      if (bodyTick) return;
      bodyTick = window.setTimeout(function () {
        bodyTick = null;
        refresh();
      }, 120);
    });
    if (document.body) bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  window.NoshRails = { version: VERSION, refresh: refresh, teardown: teardown, entries: entries };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refresh, { once: true });
  } else {
    refresh();
  }

  // Images and web fonts change track widths after first paint.
  window.addEventListener('load', onWindowResize);
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(onWindowResize).catch(function () {});
  }
})();
